import { Play } from "../../api/types";
import { apiFetch } from "../../api/client";
import { DEFAULT_CROP, parseCropJson, PlayCrop } from "../../render/crop";
import { renderPlayPng } from "../../render/renderPlayPng";
import { BoxRect, computeContainRect } from "./containRect";

export const CHLK_TAG_PLAY_ID = "CHLK_PLAY_ID";
export const CHLK_TAG_CROP = "CHLK_CROP";

const RECT_TOLERANCE = 5;

export interface SelectedChlkPlay {
  playId: string;
  crop: PlayCrop;
  rect: BoxRect;
}

function supportsTags(): boolean {
  return Office.context.requirements.isSetSupported("PowerPointApi", "1.3");
}

function supportsSelection(): boolean {
  return Office.context.requirements.isSetSupported("PowerPointApi", "1.5");
}

function shapeToRect(shape: PowerPoint.Shape): BoxRect {
  return {
    left: shape.left,
    top: shape.top,
    width: shape.width,
    height: shape.height,
  };
}

function rectMatches(a: BoxRect, b: BoxRect): boolean {
  return (
    Math.abs(a.left - b.left) <= RECT_TOLERANCE &&
    Math.abs(a.top - b.top) <= RECT_TOLERANCE &&
    Math.abs(a.width - b.width) <= RECT_TOLERANCE &&
    Math.abs(a.height - b.height) <= RECT_TOLERANCE
  );
}

async function readTagsFromShape(
  shape: PowerPoint.Shape
): Promise<{ playId: string; crop: PlayCrop } | null> {
  shape.tags.load("items");
  await shape.context.sync();

  for (const tag of shape.tags.items) {
    tag.load("key, value");
  }
  await shape.context.sync();

  let playId: string | null = null;
  let cropJson: string | null = null;

  for (const tag of shape.tags.items) {
    if (tag.key === CHLK_TAG_PLAY_ID) playId = tag.value;
    if (tag.key === CHLK_TAG_CROP) cropJson = tag.value;
  }

  if (!playId) return null;
  return { playId, crop: parseCropJson(cropJson) };
}

/** Live selection only — never falls back to cache. */
async function readLiveSelectedChlkPlay(): Promise<SelectedChlkPlay | null> {
  if (!supportsTags() || !supportsSelection()) return null;

  return PowerPoint.run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items/left,items/top,items/width,items/height");
    await context.sync();

    if (selected.items.length !== 1) return null;

    const shape = selected.items[0];
    const tags = await readTagsFromShape(shape);
    if (!tags) return null;

    return {
      playId: tags.playId,
      crop: tags.crop,
      rect: shapeToRect(shape),
    };
  });
}

export async function getSelectedChlkPlay(): Promise<SelectedChlkPlay | null> {
  try {
    return await readLiveSelectedChlkPlay();
  } catch (err) {
    console.warn("[CHLK] Live selection read failed:", err);
    return null;
  }
}

/** Find a tagged CHLK shape on the current slide by playId, optionally disambiguated by rect. */
async function findTaggedShapeByPlayId(
  playId: string,
  hintRect?: BoxRect
): Promise<SelectedChlkPlay | null> {
  if (!supportsTags() || !supportsSelection()) return null;

  return PowerPoint.run(async (context) => {
    const slides = context.presentation.getSelectedSlides();
    slides.load("items");
    await context.sync();
    if (slides.items.length === 0) return null;

    const slide = slides.getItemAt(0);
    slide.shapes.load("items/left,items/top,items/width,items/height");
    await context.sync();

    const matches: SelectedChlkPlay[] = [];
    for (const shape of slide.shapes.items) {
      const tags = await readTagsFromShape(shape);
      if (tags?.playId === playId) {
        matches.push({
          playId,
          crop: tags.crop,
          rect: shapeToRect(shape),
        });
      }
    }

    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];
    if (hintRect) {
      const hinted = matches.find((m) => rectMatches(m.rect, hintRect));
      if (hinted) return hinted;
    }
    return null;
  });
}

function applyTags(shape: PowerPoint.Shape, playId: string, crop: PlayCrop): void {
  shape.tags.add(CHLK_TAG_PLAY_ID, playId);
  shape.tags.add(CHLK_TAG_CROP, JSON.stringify(crop));
}

export async function tagShapeAtRect(
  playId: string,
  crop: PlayCrop,
  rect: BoxRect
): Promise<void> {
  if (!supportsTags() || !supportsSelection()) {
    console.warn("[CHLK] Shape tags not supported on this host.");
    return;
  }

  await PowerPoint.run(async (context) => {
    const slides = context.presentation.getSelectedSlides();
    slides.load("items");
    await context.sync();

    if (slides.items.length === 0) return;

    const slide = slides.getItemAt(0);
    const shapes = slide.shapes;
    shapes.load("items/left,items/top,items/width,items/height");
    await context.sync();

    for (const shape of shapes.items) {
      if (rectMatches(shapeToRect(shape), rect)) {
        applyTags(shape, playId, crop);
        await context.sync();
        return;
      }
    }

    console.warn("[CHLK] Could not find inserted shape at rect to tag.");
  });
}

export async function tagSelectedShape(playId: string, crop: PlayCrop): Promise<void> {
  if (!supportsTags() || !supportsSelection()) return;

  await PowerPoint.run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items/left,items/top,items/width,items/height");
    await context.sync();

    if (selected.items.length !== 1) return;

    const shape = selected.items[0];
    applyTags(shape, playId, crop);
    await context.sync();
  });
}

function insertAtCoordinates(base64: string, rect: BoxRect): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(
      base64,
      {
        coercionType: Office.CoercionType.Image,
        imageLeft: rect.left,
        imageTop: rect.top,
        imageWidth: rect.width,
        imageHeight: rect.height,
      },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Office error ${result.error.code}: ${result.error.message}`));
        } else {
          resolve();
        }
      }
    );
  });
}

async function deleteTaggedShapeAtRect(playId: string, rect: BoxRect): Promise<void> {
  if (!supportsSelection()) return;

  await PowerPoint.run(async (context) => {
    const slides = context.presentation.getSelectedSlides();
    slides.load("items");
    await context.sync();
    if (slides.items.length === 0) return;

    const slide = slides.getItemAt(0);
    slide.shapes.load("items/left,items/top,items/width,items/height");
    await context.sync();

    for (const shape of slide.shapes.items) {
      const tags = await readTagsFromShape(shape);
      if (tags?.playId === playId && rectMatches(shapeToRect(shape), rect)) {
        shape.delete();
        await context.sync();
        return;
      }
    }
  });
}

/**
 * Replace a tagged CHLK play image. Inserts the new image first so a failed insert
 * never removes the existing play. Deletes only the single tagged shape matching
 * playId + anchorRect — never whatever happens to be selected.
 *
 * @returns The rect where the new image was placed (becomes the next anchor).
 */
export async function replaceChlkPlayImage(
  playId: string,
  base64: string,
  imgW: number,
  imgH: number,
  crop: PlayCrop,
  anchorRect: BoxRect
): Promise<BoxRect> {
  const target = await findTaggedShapeByPlayId(playId, anchorRect);
  if (!target) {
    throw new Error("Select the CHLK play on the slide to update.");
  }

  const insertRect = computeContainRect(target.rect, imgW, imgH);

  await insertAtCoordinates(base64, insertRect);

  try {
    await deleteTaggedShapeAtRect(playId, target.rect);
  } catch (err) {
    console.warn("[CHLK] Could not remove previous play image after insert:", err);
  }

  await tagShapeAtRect(playId, crop, insertRect);
  return insertRect;
}

export async function resetChlkPlayMask(): Promise<void> {
  const selected = await getSelectedChlkPlay();
  if (!selected) {
    throw new Error("Select a CHLK play on the slide.");
  }

  const play = await apiFetch<Play>(`/play/${selected.playId}`);
  const { png, width, height } = await renderPlayPng(play, { crop: DEFAULT_CROP });
  await replaceChlkPlayImage(selected.playId, png, width, height, DEFAULT_CROP, selected.rect);
}
